import type { PropsWithChildren, JSX } from 'react';

import { cx } from '@emotion/css';
import { Col, Row } from 'antd';
import * as _ from 'lodash';
import { useMediaQuery } from 'react-responsive';
import { Link } from 'react-router-dom';

import { Color, Image, ScreenBreakpoint, Spacing } from '../../../styles';
import Card from '../../Miscellaneous/Card';
import { MTypography } from '../../MTypography/MTypography';

import * as Styles from './CardLayout.styles';

interface CardLayoutProps {
  title?: string;
  titleAlign?: 'center' | 'left';
  subtitle?: string | JSX.Element;
  subtitleAlign?: 'center' | 'left';
  afterSubtitle?: JSX.Element;
  afterSubtitleAlign?: 'center' | 'left';
  activeTab?: string;
  tabs?: string[];
  footer?: JSX.Element;
  footerAlign?: 'center' | 'left';
  cardTitle?: string;
  cardSubtitle?: string | JSX.Element;
  cardClassName?: string;
  logoUrl?: string;
  logoPath?: string;
  rightSlot?: JSX.Element;
}

export const CardLayout = ({
  children,
  activeTab,
  title,
  titleAlign = 'center',
  subtitle,
  subtitleAlign = 'center',
  afterSubtitle,
  afterSubtitleAlign = 'center',
  tabs = [],
  footer,
  footerAlign = 'center',
  cardTitle,
  cardSubtitle,
  cardClassName = '',
  logoPath,
  logoUrl,
  rightSlot,
}: PropsWithChildren<CardLayoutProps>) => {
  const isMobileOrTablet = useMediaQuery({ query: `(max-width: ${ScreenBreakpoint.laptop.max})` });

  const renderTabsIfFound = () => {
    const tabElements = tabs.map((aTab: string) => {
      return (
        <span className={activeTab === aTab ? Styles.activeTab : Styles.tab} key={aTab}>
          {aTab}
        </span>
      );
    });

    if (!_.isEmpty(tabElements)) {
      return <div className={Styles.tabs}> {tabElements}</div>;
    }

    return null;
  };

  const renderTitle = (): JSX.Element | null =>
    title ? (
      <Row justify='center'>
        <Col xs={24} md={12} className={cx(Styles.title, Spacing.mb16, titleAlign === 'left' && Styles.alignLeft)}>
          <MTypography variant='h6' weight='bold' color={Color.Brand6} testId='login-title-testId'>
            {title}
          </MTypography>
        </Col>
      </Row>
    ) : null;

  const renderSubtitle = (): JSX.Element | null =>
    subtitle ? (
      <Row justify='center'>
        <Col
          xs={24}
          md={12}
          className={cx(
            Styles.subtitle,
            afterSubtitle ? undefined : Spacing.mb40,
            subtitleAlign === 'left' && Styles.alignLeft,
          )}
          data-testid='login-subtitle-testId'>
          {typeof subtitle === 'string' ? <MTypography variant='subtitle1'>{subtitle}</MTypography> : subtitle}
        </Col>
      </Row>
    ) : null;

  const renderAfterSubtitle = (): JSX.Element | null =>
    afterSubtitle ? (
      <Row justify='center'>
        <Col xs={24} md={12} className={cx(Styles.afterSubtitle, afterSubtitleAlign === 'left' && Styles.alignLeft)}>
          {afterSubtitle}
        </Col>
      </Row>
    ) : null;

  const renderFooterIfFound = (): JSX.Element | null =>
    footer ? (
      <Row justify='center'>
        <Col xs={24} md={12} className={cx(Styles.footer, footerAlign === 'left' && Styles.alignLeft)}>
          {footer}
        </Col>
      </Row>
    ) : null;

  const renderCardTitleIfFound = (): JSX.Element | null =>
    cardTitle ? (
      <MTypography variant='h4' weight='semibold' color={Color.Gray10} className={Styles.cardTitle}>
        {cardTitle}
      </MTypography>
    ) : null;

  const renderCardSubtitleIfFound = (): JSX.Element | null =>
    cardSubtitle ? <div className={Styles.cardSubtitle}>{cardSubtitle}</div> : null;

  return (
    <div>
      <div className={Styles.container}>
        <div className={Styles.main}>
          <Row justify='center' className={Styles.logoContainer}>
            <Col flex='0 1 auto' className={Styles.logoCol}>
              <Link to={logoPath ?? '/'}>
                <img
                  src={logoUrl ?? Image.MyIPOLogoSVG}
                  alt='My IPO'
                  className={cx(Styles.logo, logoUrl ? undefined : Styles.brandLogo)}
                  data-testid='card-layout-page-img-logo-testId'
                />
              </Link>
            </Col>
          </Row>
          {renderTitle()}
          {renderSubtitle()}
          {renderAfterSubtitle()}
          {isMobileOrTablet && rightSlot ? (
            <Row justify='center' className={Spacing.mb24}>
              <Col xs={24} md={12}>
                {rightSlot}
              </Col>
            </Row>
          ) : null}
          {renderTabsIfFound()}
          <Row className={Styles.cardContainer} justify='center' align='middle'>
            <Card className={Styles.card.concat(` ${cardClassName}`).trim()}>
              <div className={Styles.cardContentContainer}>
                {renderCardTitleIfFound()}
                {renderCardSubtitleIfFound()}
                {children}
              </div>
            </Card>
            {!isMobileOrTablet && rightSlot ? <Col className={Styles.rightSlot}>{rightSlot}</Col> : null}
          </Row>
          {renderFooterIfFound()}
        </div>
      </div>
    </div>
  );
};
